import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Repeat, Loader2, Check, Camera, X, Info, Landmark, ChevronsUpDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CurrencyInput, NumberInput } from '../components/shared/CurrencyInput';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { resolveBuyerTheme, TAP_BLUE } from '../components/showroom/buyerTheme';
import { attachTactile, feedback, setOrgTactile } from '../components/showroom/tactile';
import { buyerSessionId, useFormDraft, useSessionContact, mergeKnown } from '../components/showroom/useFormDraft';
import { vehiclePublicAPI } from '../lib/api';
import useBuyerHeartbeat from '../components/showroom/useBuyerHeartbeat';

const API = (typeof window !== 'undefined' ? window.location.origin : '');
// Uploads come back relative; a seeded or migrated photo may be absolute.
const photoSrc = (u) => (String(u).startsWith('http') ? u : `${API}${u}`);
const CONSENT_VERSION = 'vehicle-2026-09-v1';
const CONDITIONS = ['excellent', 'good', 'fair', 'rough'];
const MAX_PHOTOS = 8;

const Chip = ({ active, onClick, children, testid, th }) => (
    <button type="button" onClick={onClick} data-testid={testid}
        className={`h-11 px-4 rounded-xl border text-sm font-semibold transition-colors ${active
            ? 'border-emerald-400 bg-emerald-500/20 text-emerald-100'
            : th.pickBtn}`}>
        {children}
    </button>
);

/** Searchable picker for Make / Model.
 *  Replaces <Input list>+<datalist>: native datalist renders NO visible
 *  suggestions on iOS Safari, so real fetched data looked like a plain text
 *  box. This is the Popover + Command combobox pattern already used elsewhere
 *  in the app — fully DOM-rendered, so it looks and behaves the same on every
 *  platform. 400+ makes make a plain <select> unusable, hence the search.
 *  Whatever the visitor types is always acceptable (free text is never blocked). */
const SearchPicker = ({ value, onChange, options, placeholder, searchPlaceholder, emptyHint, useTyped, testid, th }) => {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const query = q.trim().toLowerCase();
    const shown = useMemo(() => (
        (query ? options.filter((o) => o.toLowerCase().includes(query)) : options).slice(0, 80)
    ), [options, query]);
    const pick = (val) => { onChange(val); setQ(''); setOpen(false); };
    return (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(''); }}>
            <PopoverTrigger asChild>
                <button type="button" data-testid={testid}
                    className={`w-full h-12 rounded-xl px-3 flex items-center justify-between gap-2 text-sm ${th.input}`}>
                    <span className={`truncate ${value ? '' : 'opacity-60'}`} data-testid={`${testid}-value`}>
                        {value || placeholder}
                    </span>
                    <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[min(22rem,88vw)]" align="start">
                <Command shouldFilter={false}>
                    <CommandInput placeholder={searchPlaceholder} value={q} onValueChange={setQ}
                        data-testid={`${testid}-search`} />
                    <CommandList className="max-h-64">
                        <CommandEmpty>
                            {q.trim()
                                ? <button type="button" onClick={() => pick(q.trim())} data-testid={`${testid}-use-typed`}
                                    className="w-full text-left px-2 py-1.5 text-sm font-semibold">
                                    {useTyped.replace('{{value}}', q.trim())}
                                </button>
                                : <span className="px-2 py-1.5 text-sm block">{emptyHint}</span>}
                        </CommandEmpty>
                        <CommandGroup>
                            {shown.map((o) => (
                                <CommandItem key={o} value={o} onSelect={() => pick(o)}
                                    data-testid={`${testid}-option-${o.replace(/\s+/g, '-').toLowerCase()}`}>
                                    <Check className={`w-4 h-4 mr-2 ${o === value ? 'opacity-100' : 'opacity-0'}`} />
                                    {o}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

/** Trade-In Estimator — full-screen takeover, never a modal.
 *  Deliberately an APPRAISAL REQUEST: no book-value provider is wired, and a
 *  made-up number would poison the first conversation with the desk. */
export default function TradeInEstimator() {
    const { cardId } = useParams();
    const navigate = useNavigate();
    const rootRef = useRef(null);
    useEffect(() => attachTactile(document.body), []);
    const { t } = useTranslation();

    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [form, setForm] = useState({ year: '', make: '', model: '', trim: '', mileage: '', vin: '', condition: '', payoff: '', notes: '' });
    const [years, setYears] = useState([]);
    const [makes, setMakes] = useState([]);
    const [models, setModels] = useState([]);
    // Trims for the chosen year/make/model (Auto.dev listings + this dealer's
    // own inventory / appraised trade-ins, merged server-side). Empty list =>
    // the field silently stays free text, like Make/Model already do.
    const [trims, setTrims] = useState([]);
    const [trimOther, setTrimOther] = useState(false);
    const [lookupDown, setLookupDown] = useState(false);
    const [vinBusy, setVinBusy] = useState(false);
    const [photos, setPhotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [contact, setContact] = useState({ name: '', email: '', phone: '' });
    const [needContact, setNeedContact] = useState(false);
    const [consent, setConsent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);

    // SAME session as the F&I screen (and honoured from `?s=`), so contact
    // details are reused and a saved link resumes this form.
    const sessionId = useMemo(() => buyerSessionId(cardId), [cardId]);
    useBuyerHeartbeat(cardId, sessionId);
    const { resumed, saveDraft } = useFormDraft(cardId, sessionId, 'trade_in');
    // One identity per visit: if they already gave these on another screen,
    // do not ask again. Their own typing always wins (mergeKnown).
    const known = useSessionContact(cardId, sessionId);
    useEffect(() => {
        if (known) setForm((f) => mergeKnown(f, known, [['name', 'name'], ['email', 'email'], ['phone', 'phone']]));
    }, [known]);

    useEffect(() => {
        axios.get(`${API}/api/vehicle/${cardId}`)
            .then((r) => { setOrgTactile(r.data?.action_noise); setData(r.data); })
            .catch(() => setErr('load'));
        // Live signal: the desk sees "Started a trade-in appraisal" while they
        // are still filling it in, not only when they submit.
        vehiclePublicAPI.activity(cardId, 'trade_in_opened', sessionId).catch(() => {});
        axios.get(`${API}/api/vehicle-lookup/years`).then((r) => setYears(r.data?.years || [])).catch(() => setYears([]));
        axios.get(`${API}/api/vehicle-lookup/makes`)
            .then((r) => { setMakes(r.data?.makes || []); if (r.data?.source === 'unavailable') setLookupDown(true); })
            .catch(() => setLookupDown(true));
    }, [cardId]);

    // Models depend on year + make; a miss simply leaves the field free-text.
    useEffect(() => {
        if (!form.year || !form.make) { setModels([]); return; }
        axios.get(`${API}/api/vehicle-lookup/models`, { params: { make: form.make, year: form.year } })
            .then((r) => setModels(r.data?.models || []))
            .catch(() => setModels([]));
    }, [form.year, form.make]);

    // Trims depend on year + make + model. A miss or a provider outage leaves
    // the field exactly as it was before this feature: free text.
    useEffect(() => {
        if (!form.year || !form.make || !form.model) { setTrims([]); return; }
        axios.get(`${API}/api/vehicle-lookup/trims`, {
            params: { year: form.year, make: form.make, model: form.model, card_id: cardId },
        })
            .then((r) => setTrims(r.data?.trims || []))
            .catch(() => setTrims([]));
        setTrimOther(false);
    }, [form.year, form.make, form.model, cardId]);

    useEffect(() => {
        if (!resumed) return;
        if (resumed.form) setForm((f) => ({ ...f, ...resumed.form }));
        if (resumed.photos?.length) setPhotos(resumed.photos);
        if (resumed.contact) setContact((c) => ({ ...c, ...resumed.contact }));
        if (resumed.needContact) setNeedContact(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resumed]);

    useEffect(() => {
        if (!data) return;
        const hasContent = Object.values(form || {}).some((v) => String(v || '').trim()) || photos.length;
        if (!hasContent) return;
        saveDraft({ form, photos, contact, needContact },
                  { name: contact.name || null, email: contact.email || null, phone: contact.phone || null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, photos, contact, needContact, data]);

    const th = resolveBuyerTheme(data?.buyer_theme);
    // The last entry of the Trim list — "Other" keeps free text reachable for
    // anything a market list cannot know about.
    const OTHER_TRIM = t('tradeIn.trimOther', 'Other — type it in');
    const consentText = t('tradeIn.consent', 'I agree to be contacted about my trade-in and this vehicle.');

    const decodeVin = async () => {
        const vin = (form.vin || '').trim();
        if (vin.length < 11) { toast.error(t('tradeIn.vinShort', "That VIN doesn't look right")); return; }
        setVinBusy(true);
        try {
            const r = await axios.get(`${API}/api/vehicle-lookup/vin/${vin}`);
            const d = r.data?.decoded || {};
            if (!d.make) { toast.error(t('tradeIn.vinNoMatch', "We couldn't decode that VIN — please pick it manually")); return; }
            setForm((f) => ({ ...f, year: String(d.year || f.year), make: d.make || f.make, model: d.model || f.model, trim: d.trim || f.trim }));
            toast.success(t('tradeIn.vinFound', 'Found it — check the details below'));
        } catch {
            toast.error(t('tradeIn.vinNoMatch', "We couldn't decode that VIN — please pick it manually"));
        } finally { setVinBusy(false); }
    };

    const addPhotos = async (files) => {
        const list = Array.from(files || []).slice(0, MAX_PHOTOS - photos.length);
        if (!list.length) return;
        setUploading(true);
        for (const file of list) {
            const fd = new FormData();
            fd.append('file', file);
            try {
                const r = await axios.post(`${API}/api/vehicle/${cardId}/upload`, fd);
                if (r.data?.url) setPhotos((p) => [...p, r.data.url]);
            } catch (e) {
                toast.error(e.response?.data?.detail || t('tradeIn.photoFailed', 'That photo would not upload'));
            }
        }
        setUploading(false);
    };

    const submit = async () => {
        if (!form.year || !form.make.trim() || !form.model.trim()) {
            toast.error(t('tradeIn.needVehicle', 'Add the year, make and model of your trade-in')); return;
        }
        if (!consent) { toast.error(t('tradeIn.consentRequired', 'Please agree to be contacted to continue')); return; }
        if (needContact && !contact.email.trim() && !contact.phone.trim()) {
            toast.error(t('tradeIn.contactRequired', 'Add an email or a mobile number so the desk can reply')); return;
        }
        setBusy(true);
        try {
            const r = await axios.post(`${API}/api/vehicle/${cardId}/trade-in`, {
                year: Number(form.year), make: form.make.trim(), model: form.model.trim(),
                trim: form.trim.trim() || null,
                mileage: form.mileage ? Number(String(form.mileage).replace(/\D/g, '')) : null,
                vin: form.vin.trim() || null, condition: form.condition || null,
                payoff_amount: form.payoff ? Number(String(form.payoff).replace(/[^\d.]/g, '')) : null,
                photos, notes: form.notes.trim() || null,
                name: contact.name || null, email: contact.email || null, phone: contact.phone || null,
                consent: true, consent_text: consentText, consent_version: CONSENT_VERSION,
                session_id: sessionId,
            });
            setDone(r.data || {});
            feedback('success');
        } catch (e) {
            feedback('error');
            if (e.response?.status === 422) {
                setNeedContact(true);
                toast.error(t('tradeIn.contactRequired', 'Add an email or a mobile number so the desk can reply'));
            } else {
                toast.error(e.response?.data?.detail || t('tradeIn.error', 'Could not send that — please try again'));
            }
        } finally { setBusy(false); }
    };

    if (err) {
        return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-300" data-testid="trade-in-error">
            {t('tradeIn.unavailable', 'This page is unavailable')}
        </div>;
    }
    if (!data) {
        return <div className="min-h-screen flex items-center justify-center bg-neutral-950" data-testid="trade-in-loading">
            <Loader2 className="w-7 h-7 animate-spin text-neutral-500" />
        </div>;
    }

    const v = data.vehicle || {};
    const title = [v.year, v.make, v.model].filter(Boolean).join(' ');

    return (
        <div ref={rootRef} className={`min-h-screen ${th.page}`} data-testid="trade-in-page">
            <div className="max-w-md mx-auto px-4 pt-5 pb-24 space-y-4">
                <button onClick={() => navigate(`/vehicle/${cardId}`)} data-testid="trade-in-back"
                    className={`flex items-center gap-1 text-sm ${th.muted} hover:opacity-80`}>
                    <ArrowLeft className="w-4 h-4" />{t('common.back', 'Back')}
                </button>

                <header>
                    <h1 className={`text-2xl font-extrabold flex items-center gap-2 ${th.title}`} data-testid="trade-in-title">
                        <Repeat className="w-6 h-6" style={{ color: TAP_BLUE }} />
                        {t('tradeIn.title', 'Trade-in appraisal')}
                    </h1>
                    <p className={`text-sm mt-1 ${th.muted}`} data-testid="trade-in-context">
                        {t('tradeIn.against', 'Against {{vehicle}}', { vehicle: title })}
                    </p>
                </header>

                {done ? (
                    <section className={`rounded-2xl p-5 text-center space-y-2 ${th.panelSolid}`} data-testid="trade-in-done">
                        <Check className="w-7 h-7 text-emerald-400 mx-auto" />
                        <p className={`text-base font-bold ${th.title}`}>{t('tradeIn.sentTitle', 'Your trade-in is with the desk')}</p>
                        <p className={`text-sm ${th.muted}`} data-testid="trade-in-done-summary">{done.summary}</p>
                        <p className={`text-xs ${th.faint}`}>{t('tradeIn.sentHint', 'They will come back to you with a number. Nothing is committed.')}</p>
                        <Button onClick={() => navigate(`/vehicle/${cardId}`)} data-testid="trade-in-done-back"
                            className="w-full h-12 rounded-2xl font-bold text-white mt-2" style={{ backgroundColor: TAP_BLUE }}>
                            {t('tradeIn.backToVehicle', 'Back to the vehicle')}
                        </Button>
                    </section>
                ) : (
                    <>
                        <p className={`flex items-start gap-2 text-xs leading-relaxed ${th.faint}`} data-testid="trade-in-disclaimer">
                            <Info className="w-4 h-4 mt-0.5 shrink-0" />
                            {t('tradeIn.disclaimer', 'This is an appraisal request, not an offer. The desk reviews your details and comes back with a number — no instant valuation, no obligation.')}
                        </p>

                        {/* VIN shortcut */}
                        <section className={`rounded-2xl p-4 space-y-2 ${th.panelSolid}`} data-testid="trade-in-vin-section">
                            <Label className={th.label}>{t('tradeIn.vin', 'VIN (fastest way)')}</Label>
                            <div className="flex gap-2">
                                <Input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })}
                                    placeholder={t('tradeIn.vinPlaceholder', 'Optional — 17 characters')}
                                    className={`h-12 ${th.input}`} data-testid="trade-in-vin" />
                                <Button onClick={decodeVin} disabled={vinBusy} variant="outline"
                                    className="h-12 shrink-0" data-testid="trade-in-vin-decode">
                                    {vinBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('tradeIn.vinDecode', 'Find')}
                                </Button>
                            </div>
                        </section>

                        {/* Year / Make / Model */}
                        <section className={`rounded-2xl p-4 space-y-3 ${th.panelSolid}`} data-testid="trade-in-vehicle-section">
                            <div className="space-y-1.5">
                                <Label className={th.label}>{t('tradeIn.year', 'Year')}</Label>
                                <select value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value, model: '' })}
                                    className={`w-full h-12 rounded-xl px-3 text-sm ${th.input}`} data-testid="trade-in-year">
                                    <option value="">{t('tradeIn.choose', 'Choose…')}</option>
                                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                            <div className="space-y-1.5">
                                <Label className={th.label}>{t('tradeIn.make', 'Make')}</Label>
                                {/* Real picker when the lookup is answering; honest free
                                    text when it is not (unchanged fallback). */}
                                {(!lookupDown && makes.length) ? (
                                    <SearchPicker th={th} value={form.make} options={makes}
                                        onChange={(v) => setForm({ ...form, make: v, model: '' })}
                                        placeholder={t('tradeIn.choose', 'Choose…')}
                                        searchPlaceholder={t('tradeIn.searchMake', 'Search makes…')}
                                        emptyHint={t('tradeIn.typeToSearch', 'Type to search')}
                                        useTyped={t('tradeIn.useTyped', 'Use “{{value}}”')}
                                        testid="trade-in-make" />
                                ) : (
                                    <Input value={form.make}
                                        onChange={(e) => setForm({ ...form, make: e.target.value, model: '' })}
                                        placeholder={t('tradeIn.makeFree', 'Type the make')}
                                        className={`h-12 ${th.input}`} data-testid="trade-in-make" />
                                )}
                            </div>
                            <div className="space-y-1.5">
                                <Label className={th.label}>{t('tradeIn.model', 'Model')}</Label>
                                {models.length ? (
                                    <SearchPicker th={th} value={form.model} options={models}
                                        onChange={(v) => setForm({ ...form, model: v })}
                                        placeholder={t('tradeIn.choose', 'Choose…')}
                                        searchPlaceholder={t('tradeIn.searchModel', 'Search models…')}
                                        emptyHint={t('tradeIn.typeToSearch', 'Type to search')}
                                        useTyped={t('tradeIn.useTyped', 'Use “{{value}}”')}
                                        testid="trade-in-model" />
                                ) : (
                                    <Input value={form.model}
                                        onChange={(e) => setForm({ ...form, model: e.target.value })}
                                        placeholder={t('tradeIn.modelFree', 'Type the model')}
                                        className={`h-12 ${th.input}`} data-testid="trade-in-model" />
                                )}
                            </div>
                            <div className="space-y-1.5">
                                <Label className={th.label}>{t('tradeIn.trim', 'Trim (optional)')}</Label>
                                {/* A real list when we have one for THIS vehicle (market
                                    listings + this dealer's own inventory / appraised
                                    trade-ins, merged server-side), with "Other" at the end
                                    for anything the list misses. No list → free text, same
                                    as before. The VIN decode still pre-fills its value. */}
                                {(trims.length && !trimOther) ? (
                                    <SearchPicker th={th} value={form.trim}
                                        options={[...trims, OTHER_TRIM]}
                                        onChange={(v) => {
                                            if (v === OTHER_TRIM) { setTrimOther(true); setForm({ ...form, trim: '' }); return; }
                                            setForm({ ...form, trim: v });
                                        }}
                                        placeholder={t('tradeIn.choose', 'Choose…')}
                                        searchPlaceholder={t('tradeIn.searchTrim', 'Search trims…')}
                                        emptyHint={t('tradeIn.typeToSearch', 'Type to search')}
                                        useTyped={t('tradeIn.useTyped', 'Use “{{value}}”')}
                                        testid="trade-in-trim" />
                                ) : (
                                    <>
                                        <Input value={form.trim} onChange={(e) => setForm({ ...form, trim: e.target.value })}
                                            placeholder={t('tradeIn.trimPlaceholder', 'e.g. Premium Plus')}
                                            className={`h-12 ${th.input}`} data-testid="trade-in-trim" />
                                        {(trims.length && trimOther) ? (
                                            <button type="button" onClick={() => { setTrimOther(false); setForm({ ...form, trim: '' }); }}
                                                data-testid="trade-in-trim-back"
                                                className={`text-xs underline underline-offset-2 ${th.faint}`}>
                                                {t('tradeIn.trimBackToList', 'Choose from the list instead')}
                                            </button>
                                        ) : null}
                                    </>
                                )}
                            </div>
                            {lookupDown && (
                                <p className={`text-xs ${th.faint}`} data-testid="trade-in-lookup-down">
                                    {t('tradeIn.lookupDown', 'The vehicle database is not answering right now — just type the details in.')}
                                </p>
                            )}
                        </section>

                        {/* Condition / mileage / payoff */}
                        <section className={`rounded-2xl p-4 space-y-3 ${th.panelSolid}`} data-testid="trade-in-condition-section">
                            <div className="space-y-1.5">
                                <Label className={th.label}>{t('tradeIn.mileage', 'Mileage (km)')}</Label>
                                <NumberInput value={form.mileage}
                                    onChange={(v) => setForm({ ...form, mileage: v })}
                                    placeholder={t('tradeIn.mileagePlaceholder', 'e.g. 34,000')}
                                    className={`h-12 ${th.input}`} data-testid="trade-in-mileage" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className={th.label}>{t('tradeIn.condition', 'Condition')}</Label>
                                <div className="flex flex-wrap gap-2" data-testid="trade-in-conditions">
                                    {CONDITIONS.map((c) => (
                                        <Chip key={c} th={th} active={form.condition === c} testid={`trade-in-condition-${c}`}
                                            onClick={() => setForm({ ...form, condition: form.condition === c ? '' : c })}>
                                            {t(`tradeIn.cond.${c}`, c)}
                                        </Chip>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label className={th.label}>{t('tradeIn.payoff', 'Loan or lease payoff (optional)')}</Label>
                                <CurrencyInput value={form.payoff}
                                    onChange={(v) => setForm({ ...form, payoff: v })}
                                    placeholder={t('tradeIn.payoffPlaceholder', 'e.g. 12,500')}
                                    className={`h-12 ${th.input}`} data-testid="trade-in-payoff" />
                            </div>
                        </section>

                        {/* Photos */}
                        <section className={`rounded-2xl p-4 space-y-3 ${th.panelSolid}`} data-testid="trade-in-photos-section">
                            <Label className={th.label}>{t('tradeIn.photos', 'Photos ({{n}}/{{max}})', { n: photos.length, max: MAX_PHOTOS })}</Label>
                            <p className={`text-xs ${th.faint}`}>{t('tradeIn.photosHint', 'Four corners and the odometer give the sharpest number.')}</p>
                            <div className="flex flex-wrap gap-2" data-testid="trade-in-photo-list">
                                {photos.map((url, i) => (
                                    <div key={url} className="relative w-20 h-20 rounded-xl overflow-hidden border border-white/10" data-testid={`trade-in-photo-${i}`}>
                                        <img src={photoSrc(url)} alt="" className="w-full h-full object-cover" />
                                        <button onClick={() => setPhotos(photos.filter((p) => p !== url))}
                                            data-testid={`trade-in-photo-remove-${i}`}
                                            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center">
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                                {photos.length < MAX_PHOTOS && (
                                    <label className={`w-20 h-20 rounded-xl border border-dashed flex flex-col items-center justify-center cursor-pointer ${th.pickBtn}`}
                                        data-testid="trade-in-photo-add">
                                        {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                                        <span className="text-[10px] mt-1">{t('tradeIn.addPhoto', 'Add')}</span>
                                        <input type="file" accept="image/*" capture="environment" multiple className="hidden"
                                            onChange={(e) => addPhotos(e.target.files)} data-testid="trade-in-photo-input" />
                                    </label>
                                )}
                            </div>
                        </section>

                        {/* Notes + contact + consent */}
                        <section className={`rounded-2xl p-4 space-y-3 ${th.panelSolid}`} data-testid="trade-in-submit-section">
                            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                placeholder={t('tradeIn.notes', 'Anything the desk should know? (optional)')}
                                className={`w-full rounded-xl p-3 text-sm ${th.input}`} data-testid="trade-in-notes" />
                            {needContact && (
                                <>
                                    <Input className={`h-12 ${th.input}`} placeholder={t('tradeIn.name', 'Your name')}
                                        value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })}
                                        data-testid="trade-in-name" />
                                    <Input className={`h-12 ${th.input}`} placeholder={t('tradeIn.email', 'Email')}
                                        value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
                                        data-testid="trade-in-email" />
                                    <Input className={`h-12 ${th.input}`} placeholder={t('tradeIn.phone', 'Mobile number')}
                                        value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                                        data-testid="trade-in-phone" />
                                </>
                            )}
                            <label className={`flex items-start gap-2 text-sm cursor-pointer ${th.muted}`}>
                                <Checkbox checked={consent} onCheckedChange={setConsent} className="mt-0.5" data-testid="trade-in-consent" />
                                <span data-testid="trade-in-consent-text">{consentText}</span>
                            </label>
                            <Button onClick={submit} disabled={busy} data-testid="trade-in-submit"
                                className="w-full h-12 rounded-2xl font-bold text-white hover:opacity-90"
                                style={{ backgroundColor: TAP_BLUE }}>
                                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                {t('tradeIn.submit', 'Send for appraisal')}
                            </Button>
                            <button onClick={() => navigate(`/vehicle/${cardId}?save=1`)} data-testid="trade-in-save-later"
                                className={`w-full text-center text-xs font-semibold ${th.muted} hover:opacity-80`}>
                                {t('tradeIn.saveLater', 'Save and finish later')}
                            </button>
                            {data.fi?.financing || data.fi?.warranty || data.fi?.insurance ? (
                                <button onClick={() => navigate(`/vehicle/${cardId}/finance`)} data-testid="trade-in-to-fi"
                                    className={`w-full flex items-center justify-center gap-1.5 text-xs font-semibold pt-1 ${th.muted} hover:opacity-80`}>
                                    <Landmark className="w-3.5 h-3.5" />{t('tradeIn.toFi', 'See payments with a trade-in')}
                                </button>
                            ) : null}
                        </section>
                    </>
                )}
            </div>
        </div>
    );
}
